/**
 * American BioLabs — Google Ads capture-to-order bridge.
 *
 * Captures Google click identifiers and campaign parameters that arrive on the
 * Shopify storefront, persists them only when Shopify allows marketing
 * processing, and mirrors them to private Shopify cart attributes. Those cart
 * attributes carry into the order for later server-side conversion matching.
 *
 * This module is deliberately independent of product forms, Recharge, cart
 * rendering, and checkout routing. It never replaces or intercepts cart adds.
 */
(function () {
  'use strict';

  if (window.ABLGoogleAttribution) return;

  var VERSION = '1.0.0';
  var STORAGE_KEY = 'abl_google_attribution_v1';
  var SESSION_SYNC_KEY = 'abl_google_attribution_synced_v1';
  var COOKIE_NAME = 'abl_google_attribution';
  var COOKIE_MAX_AGE = 90 * 24 * 60 * 60;
  var MAX_VALUE_LENGTH = 512;

  var PARAMETER_KEYS = [
    'gclid',
    'wbraid',
    'gbraid',
    'gad_source',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  var ATTRIBUTE_NAMES = {
    gclid: '__gclid',
    wbraid: '__wbraid',
    gbraid: '__gbraid',
    gad_source: '__gad_source',
    utm_source: '__utm_source',
    utm_medium: '__utm_medium',
    utm_campaign: '__utm_campaign',
    utm_term: '__utm_term',
    utm_content: '__utm_content'
  };

  var incoming = readIncomingParameters();
  var attribution = null;
  var marketingAllowed = false;
  var consentResolved = false;
  var privacyLoadRequested = false;
  var privacyAttempts = 0;
  var pendingSync = null;
  var retriesRemaining = 1;

  function cleanValue(value) {
    if (typeof value !== 'string') return '';
    var cleaned = value.replace(/[\u0000-\u001F\u007F]/g, '').trim();
    if (!cleaned || /^(?:undefined|null)$/i.test(cleaned) || /^\{[^}]+\}$/.test(cleaned)) return '';
    return cleaned.slice(0, MAX_VALUE_LENGTH);
  }

  function readIncomingParameters() {
    var values = {};
    var search;

    try {
      search = new URLSearchParams(window.location.search || '');
    } catch (error) {
      return values;
    }

    PARAMETER_KEYS.forEach(function (key) {
      var value = cleanValue(search.get(key));
      if (value) values[key] = value;
    });

    /* CheckoutChamp historically forwarded this alias. Treat it as a GCLID
       only when the canonical gclid parameter is absent. */
    if (!values.gclid) {
      var forwardedGclid = cleanValue(search.get('cc_custom_gclid'));
      if (forwardedGclid) values.gclid = forwardedGclid;
    }

    return values;
  }

  function hasClickIdentifier(values) {
    return Boolean(values && (values.gclid || values.wbraid || values.gbraid));
  }

  function hasCampaignParameter(values) {
    if (!values) return false;
    return PARAMETER_KEYS.some(function (key) { return Boolean(values[key]); });
  }

  function normalizeRecord(candidate) {
    if (!candidate || typeof candidate !== 'object' || !candidate.parameters) return null;

    var parameters = {};
    PARAMETER_KEYS.forEach(function (key) {
      var value = cleanValue(candidate.parameters[key]);
      if (value) parameters[key] = value;
    });

    if (!hasCampaignParameter(parameters)) return null;

    return {
      version: 1,
      captured_at: cleanValue(candidate.captured_at) || new Date().toISOString(),
      parameters: parameters
    };
  }

  function createRecord(parameters) {
    return normalizeRecord({
      version: 1,
      captured_at: new Date().toISOString(),
      parameters: parameters
    });
  }

  function readCookie() {
    var prefix = COOKIE_NAME + '=';
    var cookies = document.cookie ? document.cookie.split(';') : [];

    for (var i = 0; i < cookies.length; i += 1) {
      var part = cookies[i].trim();
      if (part.indexOf(prefix) !== 0) continue;
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch (error) {
        return '';
      }
    }

    return '';
  }

  function loadStoredRecord() {
    var raw = '';

    try {
      raw = window.localStorage.getItem(STORAGE_KEY) || '';
    } catch (error) {
      raw = '';
    }

    if (!raw) raw = readCookie();
    if (!raw) return null;

    try {
      return normalizeRecord(JSON.parse(raw));
    } catch (error) {
      return null;
    }
  }

  function persistRecord(record) {
    var serialized = JSON.stringify(record);

    try {
      window.localStorage.setItem(STORAGE_KEY, serialized);
    } catch (error) {
      /* The first-party cookie remains the fallback when storage is blocked. */
    }

    var cookie = COOKIE_NAME + '=' + encodeURIComponent(serialized) +
      '; Max-Age=' + COOKIE_MAX_AGE + '; Path=/; SameSite=Lax';
    if (window.location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  }

  function clearPersistence() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(SESSION_SYNC_KEY);
    } catch (error) {
      /* Cookie removal below still runs when browser storage is unavailable. */
    }

    document.cookie = COOKIE_NAME + '=; Max-Age=0; Path=/; SameSite=Lax' +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  }

  function selectAttribution() {
    var stored = loadStoredRecord();

    /* A new Google click ID is the authoritative paid-click touch. UTM-only
       links do not overwrite an existing Google ID because this theme contains
       internal links with UTMs. */
    if (hasClickIdentifier(incoming)) return createRecord(incoming);
    if (stored) return stored;
    if (hasCampaignParameter(incoming)) return createRecord(incoming);
    return null;
  }

  function clickIdentifierType(parameters) {
    if (parameters.gclid) return 'gclid';
    if (parameters.wbraid) return 'wbraid';
    if (parameters.gbraid) return 'gbraid';
    return '';
  }

  function buildPrivateAttributes(record, clear) {
    var attributes = {};
    var parameters = clear || !record ? {} : record.parameters;

    PARAMETER_KEYS.forEach(function (key) {
      /* Sending empty values removes stale identifiers from an older cart when
         a newer Google click uses a different identifier type. */
      attributes[ATTRIBUTE_NAMES[key]] = parameters[key] || '';
    });

    attributes.__google_click_id_type = clear ? '' : clickIdentifierType(parameters);
    attributes.__google_click_captured_at = clear || !record ? '' : record.captured_at;
    attributes.__google_attribution_version = clear ? '' : '1';

    return attributes;
  }

  function cartRoute() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'cart/update.js';
  }

  function postAttributes(attributes) {
    return window.fetch(cartRoute(), {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) throw new Error('Attribution cart update failed');
      return true;
    });
  }

  function readSessionFingerprint() {
    try {
      return window.sessionStorage.getItem(SESSION_SYNC_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  function writeSessionFingerprint(value) {
    try {
      window.sessionStorage.setItem(SESSION_SYNC_KEY, value);
    } catch (error) {
      /* A blocked sessionStorage only means a harmless extra cart update. */
    }
  }

  function syncCart(force) {
    if (!consentResolved || !marketingAllowed || !attribution) return Promise.resolve(false);
    if (pendingSync) return pendingSync;

    var attributes = buildPrivateAttributes(attribution, false);
    var fingerprint = JSON.stringify(attributes);
    if (!force && readSessionFingerprint() === fingerprint) return Promise.resolve(true);

    var request = postAttributes(attributes).then(function () {
      writeSessionFingerprint(fingerprint);
      retriesRemaining = 1;
      document.dispatchEvent(new CustomEvent('abl:google-attribution-synced'));
      return true;
    }, function () {
      if (retriesRemaining > 0) {
        retriesRemaining -= 1;
        window.setTimeout(function () { syncCart(true); }, 1200);
      }
      return false;
    });

    pendingSync = request;
    request.then(function () {
      if (pendingSync === request) pendingSync = null;
    });
    return request;
  }

  function clearCartAttribution() {
    var clear = function () {
      return postAttributes(buildPrivateAttributes(null, true)).then(function () {
        return true;
      }, function () {
        return false;
      });
    };

    return pendingSync ? pendingSync.then(clear, clear) : clear();
  }

  function injectCheckoutAttributes(event) {
    if (!marketingAllowed || !attribution) return;
    if (!(event.target instanceof HTMLFormElement)) return;

    /* Older Safari versions can omit SubmitEvent.submitter. The focused
       checkout control is a safe fallback and does not alter form behavior. */
    var submitter = event.submitter;
    if (!submitter && document.activeElement && document.activeElement.form === event.target) {
      submitter = document.activeElement;
    }
    if (!submitter || submitter.name !== 'checkout') return;

    var form = event.target;
    var attributes = buildPrivateAttributes(attribution, false);

    Object.keys(attributes).forEach(function (attributeName) {
      var fieldName = 'attributes[' + attributeName + ']';
      var input = form.elements.namedItem(fieldName);

      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = fieldName;
        form.appendChild(input);
      }

      input.value = attributes[attributeName];
    });
  }

  function applyConsent(allowed) {
    if (consentResolved && marketingAllowed === Boolean(allowed)) return;

    var wasAllowed = marketingAllowed;
    consentResolved = true;
    marketingAllowed = Boolean(allowed);

    if (!marketingAllowed) {
      attribution = null;
      clearPersistence();
      if (wasAllowed) clearCartAttribution();
      return;
    }

    attribution = selectAttribution();
    if (!attribution) return;
    persistRecord(attribution);
    syncCart(false);
  }

  function resolveConsentFromApi() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.marketingAllowed !== 'function') return false;
    applyConsent(privacy.marketingAllowed());
    return true;
  }

  function initializePrivacy() {
    if (consentResolved) return;
    if (resolveConsentFromApi()) return;

    var shopify = window.Shopify;
    if (!privacyLoadRequested && shopify && typeof shopify.loadFeatures === 'function') {
      privacyLoadRequested = true;
      shopify.loadFeatures([
        { name: 'consent-tracking-api', version: '0.1' }
      ], function (error) {
        if (!error) resolveConsentFromApi();
      });
    }

    if (privacyAttempts < 20 && !consentResolved) {
      privacyAttempts += 1;
      window.setTimeout(initializePrivacy, 250);
    }
  }

  document.addEventListener('visitorConsentCollected', function (event) {
    var detail = event && event.detail;
    if (detail && typeof detail.marketingAllowed === 'boolean') {
      applyConsent(detail.marketingAllowed);
    } else {
      resolveConsentFromApi();
    }
  });

  /* Existing theme events are synchronization signals only. The original cart
     request and all UI behavior remain untouched. */
  document.addEventListener('cart:added', function () { syncCart(true); });
  document.addEventListener('cart:update', function (event) {
    var data = event.detail && event.detail.data;
    if (!data || data.didError === true) return;
    var source = String(data.source || '');
    if (/product|quick/i.test(source)) syncCart(true);
  });
  document.addEventListener('submit', injectCheckoutAttributes, true);

  window.ABLGoogleAttribution = {
    version: VERSION,
    sync: function () { return syncCart(true); }
  };

  initializePrivacy();
})();
